// Botón "Llamar" de la ficha del lead (Fase 7). Dispara el click-to-call del
// CRM (POST /leads/:id/call/start) con el proveedor preferido del usuario; el
// estado de la llamada pasa a vivir en PhoneContext (widget global).
//
// Deshabilitado cuando: no hay teléfono, el softphone Telnyx no está listo,
// hay otra llamada en curso o la sesión CRM no está conectada. NOTA: el detail
// del admin-api NO trae hoy un flag de "número erróneo" (verificado en
// src/types/leadDetail.ts) — si algún día lo expone, añadir el guard aquí.
//
// Errores (nunca silenciosos): 403 del CRM → mensaje de permisos (call/start
// exige rol sales|ops|admin en el CRM); errores del softphone Telnyx → mensaje
// claro con fallback sugerido a Cloudtalk; resto → describeCrmError.

import { memo, useState } from "react";
import { CrmHttpError, describeCrmError } from "../../api/crm";
import { useCrmSession } from "../../context/CrmSessionContext";
import { usePhone } from "../../context/PhoneContext";

type Props = {
  leadUlid: string;
  phone: string | null;
  leadName: string | null;
};

/** Código del body de error del CRM (`{ error: { code, message } }`). */
function crmErrorCode(err: CrmHttpError): string | undefined {
  const body = err.body as { error?: { code?: string } } | undefined;
  return body?.error?.code;
}

function describeStartError(err: unknown, provider: string): string {
  if (err instanceof CrmHttpError) {
    if (err.status === 403) {
      return (
        "Sin permisos para llamar: tu usuario del CRM necesita rol " +
        "sales, ops o admin. Pide el rol al equipo del CRM."
      );
    }
    if (err.status === 409) {
      return "El lead está opt-out para llamadas (suprimido). No se inicia la llamada.";
    }
    if (err.status === 404 && crmErrorCode(err) === "lead_not_found") {
      return "El lead ya no existe en el CRM. Recarga la ficha.";
    }
    if (err.status === 422) {
      const code = crmErrorCode(err);
      if (code === "lead_without_phone") {
        return "El lead no tiene teléfono registrado en el CRM.";
      }
      if (code === "phone_not_e164") {
        return "El teléfono del lead no es válido (no normaliza a E.164).";
      }
      return "No se puede llamar a este lead (teléfono inválido).";
    }
    if (err.status === 502 || err.status === 503) {
      return "El servicio de telefonía no está disponible, inténtalo de nuevo en unos minutos.";
    }
    return describeCrmError(err);
  }
  const msg = err instanceof Error ? err.message : "Error inesperado";
  // Errores locales del softphone (registro Telnyx): sugiere el fallback.
  if (provider === "telnyx") {
    return `${msg} Si persiste, cambia el proveedor a Cloudtalk.`;
  }
  return msg;
}

function CallButtonInner({ leadUlid, phone, leadName }: Props) {
  const { connected } = useCrmSession();
  const {
    provider,
    prefsReady,
    softphone,
    activeCall,
    startLeadCall,
    micDenied,
    retryMicPermission,
  } = usePhone();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sin sesión CRM no hay softphone: el header del lead no mete ruido.
  if (connected !== true || !prefsReady) return null;

  const telnyxNotReady = provider === "telnyx" && softphone.status !== "idle";
  const busy = activeCall !== null && activeCall.live !== "completed";
  const disabled = !phone || pending || telnyxNotReady || busy;

  const title = !phone
    ? "El lead no tiene teléfono"
    : busy
      ? "Ya hay una llamada en curso"
      : telnyxNotReady
        ? "El softphone Telnyx no está registrado todavía"
        : `Llamar con ${provider === "telnyx" ? "Telnyx (audio en el navegador)" : "Cloudtalk"}`;

  // Con Telnyx el audio vive en el navegador: sin permiso de micrófono la llamada
  // se conecta MUDA. Avisamos claro antes de intentarla (el pre-warm ya pidió el
  // permiso al registrar; micDenied indica que se denegó).
  const micBlocked = provider === "telnyx" && micDenied;

  const onCall = async () => {
    if (!phone) return;
    if (micBlocked) {
      // Reintento CON gesto de usuario: si el estado del permiso es "preguntar"
      // (prompt descartado), el navegador SÍ vuelve a mostrar el aviso aquí.
      const granted = await retryMicPermission();
      if (!granted) {
        // Si sigue sin permiso, distinguimos "bloqueado" (el navegador ya no
        // preguntará jamás: hay que desbloquearlo a mano) de otros fallos.
        let state: string | null = null;
        try {
          const st = await navigator.permissions?.query({
            name: "microphone" as PermissionName,
          });
          state = st?.state ?? null;
        } catch {
          /* permissions API no disponible: mensaje genérico */
        }
        setError(
          state === "denied"
            ? "El micrófono está BLOQUEADO para este sitio y el navegador no volverá " +
                "a preguntar. Desbloquéalo: icono junto a la URL → Configuración del " +
                "sitio → Micrófono → Permitir, y recarga la página."
            : "Sin permiso de micrófono: acepta el aviso del navegador (o revisa que " +
                "haya un micrófono conectado) e inténtalo de nuevo.",
        );
        return;
      }
      setError(null);
    }
    setPending(true);
    setError(null);
    try {
      await startLeadCall({ leadUlid, phone, leadName });
    } catch (err) {
      setError(describeStartError(err, provider));
    } finally {
      setPending(false);
    }
  };

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
      <button
        type="button"
        onClick={() => void onCall()}
        disabled={disabled}
        title={title}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.3rem",
          padding: "0.15rem 0.6rem",
          borderRadius: "999px",
          border: "1px solid",
          borderColor: disabled ? "#dee2e6" : "#1a7f37",
          background: disabled ? "#f8f9fb" : "#1a7f37",
          color: disabled ? "#adb5bd" : "white",
          fontSize: "0.75rem",
          fontWeight: 600,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        📞 {pending ? "Llamando…" : "Llamar"}
      </button>
      {error && (
        <span role="alert" style={{ color: "#dc3545", fontSize: "0.72rem" }}>
          {error}
        </span>
      )}
    </span>
  );
}

// memo: vive en el header sticky del lead; solo re-renderiza con sus props o
// los cambios de contexto que consume.
const CallButton = memo(CallButtonInner);
export default CallButton;
